import type { Period } from "./types";

const pad = (value: number) => String(value).padStart(2, "0");

export const formatDateForInput = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export const formatDateForApi = formatDateForInput;

const startOfDay = (date: Date) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

const endOfDay = (date: Date) => {
  const next = new Date(date);
  next.setHours(23, 59, 59, 999);
  return next;
};

const daysBetween = (from: Date, to: Date) => {
  const start = startOfDay(from).getTime();
  const end = startOfDay(to).getTime();
  return Math.max(1, Math.round((end - start) / 86400000) + 1);
};

const formatShortDate = (date: Date) =>
  new Intl.DateTimeFormat("es-CO", {
    day: "2-digit",
    month: "short",
  }).format(date);

export const createCustomPeriod = (fromInput: string, toInput: string): Period => {
  const fromDate = startOfDay(new Date(`${fromInput}T00:00:00`));
  const toDate = endOfDay(new Date(`${toInput}T00:00:00`));
  const from = fromDate <= toDate ? fromDate : startOfDay(toDate);
  const to = fromDate <= toDate ? toDate : endOfDay(fromDate);

  return {
    id: "custom",
    label: `${formatShortDate(from)} - ${formatShortDate(to)}`,
    from,
    to,
    days: daysBetween(from, to),
  };
};

export const buildFinancePeriods = (): Period[] => {
  const to = endOfDay(new Date());
  const fromDaysAgo = (days: number) => {
    const date = startOfDay(to);
    date.setDate(date.getDate() - days + 1);
    return date;
  };

  const lastMonthFrom = startOfDay(new Date(to.getFullYear(), to.getMonth() - 1, 1));
  const lastMonthTo = endOfDay(new Date(to.getFullYear(), to.getMonth(), 0));

  return [
    {
      id: "this-month",
      label: "Este mes",
      from: startOfDay(new Date(to.getFullYear(), to.getMonth(), 1)),
      to,
      days: to.getDate(),
    },
    {
      id: "last-month",
      label: "Mes anterior",
      from: lastMonthFrom,
      to: lastMonthTo,
      days: daysBetween(lastMonthFrom, lastMonthTo),
    },
    {
      id: "last-7-days",
      label: "Últimos 7 días",
      from: fromDaysAgo(7),
      to,
      days: 7,
    },
    {
      id: "last-30-days",
      label: "Últimos 30 días",
      from: fromDaysAgo(30),
      to,
      days: 30,
    },
    {
      id: "all-time",
      label: "Desde que usas Fluxi",
      from: startOfDay(new Date(2020, 0, 1)),
      to,
      days: daysBetween(startOfDay(new Date(2020, 0, 1)), to),
    },
  ];
};

export const createDefaultPeriod = () =>
  buildFinancePeriods().find((period) => period.id === "last-30-days") ?? buildFinancePeriods()[0];

export const revivePeriod = (value: unknown): Period | null => {
  const parsed = value as Partial<{ id: string; label: string; from: string; to: string; days: number }>;
  if (!parsed?.id || !parsed.label || !parsed.from || !parsed.to) return null;

  const from = new Date(parsed.from);
  const to = new Date(parsed.to);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;

  return {
    id: parsed.id === "custom" ? "custom" : (parsed.id as Period["id"]),
    label: parsed.label,
    from,
    to,
    days: parsed.days ?? daysBetween(from, to),
  };
};
